import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { Button, Input } from "reactstrap";
import Select from "react-select";
import { Plus, Pencil, Trash } from "@phosphor-icons/react";
import indicadoresService from "services/IndicadoresService";
import { SendaCustomSwal } from "components/Common/SendaCustomSwal";
import AddVariableModal from "./AddVariableModal";
import CalculatedVariableModal from "./CalculatedVariableModal";

const customStyles = {
	option: (provided, state) => ({
		...provided,
		color: state.isSelected ? "blue" : "black",
		backgroundColor: "white",
		"&:hover": { backgroundColor: "#556ee6" },
	}),
	menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const typeOptions = [
	{ value: "0", label: "Meta" },
	{ value: "1", label: "Valor Apuração" },
	{ value: "2", label: "Customizada" },
];

const graphicType = [
	{ value: 0, label: "Não Exibir" },
	{ value: 1, label: "Coluna" },
	{ value: 2, label: "Linha" },
];

const totalizadorInfos = [
	{ value: "", label: "Nenhum" },
	{ value: "sum", label: "Soma" },
	{ value: "average", label: "Média" },
	{ value: "calc", label: "Cálculo" },
];

const FALLBACK_COLORS = [
	"#22c55e", "#3b82f6", "#f97316", "#06b6d4", "#eab308",
	"#ef4444", "#8b5cf6", "#ec4899", "#14b8a6", "#f59e0b",
	"#6366f1", "#10b981",
];

const getFallbackColorForConfig = (config) => {
	const key = String(config?.id ?? "");
	let hash = 0;
	for (let i = 0; i < key.length; i++) {
		hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
	}
	return FALLBACK_COLORS[hash % FALLBACK_COLORS.length];
};

const VariableConfiguration = forwardRef(({ goalId, indicatorPeriodicity }, ref) => {
	const [configs, setConfigs] = useState([]);
	const [loading, setLoading] = useState(false);
	const [isAddModalOpen, setIsAddModalOpen] = useState(false);
	const [isCalcModalOpen, setIsCalcModalOpen] = useState(false);
	const [editingCalculatedConfig, setEditingCalculatedConfig] = useState(null);

	const isLocalMode = !goalId;

	const lastCheckedPeriodicityRef = React.useRef(null);
	const pendingRemoveIdsRef = React.useRef(new Set());
	const pendingUpdatesRef = React.useRef({});

	useEffect(() => {
		if (goalId) {
			fetchConfigs();
		}
	}, [goalId]);

	// Se o usuário trocar a periodicidade do indicador, validar as variáveis importadas
	// e remover as que não têm a mesma periodicidade.
	useEffect(() => {
		if (!indicatorPeriodicity) return;
		if (!configs || configs.length === 0) return;

		const normalizedIndicator = String(indicatorPeriodicity).trim().toLowerCase();
		if (!normalizedIndicator) return;

		// Evita rodar repetidamente quando setConfigs disparar re-render
		if (lastCheckedPeriodicityRef.current === normalizedIndicator) return;

		lastCheckedPeriodicityRef.current = normalizedIndicator;

		const importedVariableConfigs = configs.filter(
			(c) => !!c.goals_variables_id && c.GoalsVariable,
		);

		// Só considerar configs onde sabemos a periodicidade da variável
		const mismatched = importedVariableConfigs.filter((c) => {
			const varP = String(c.GoalsVariable.periodicity || "")
				.trim()
				.toLowerCase();
			if (!varP) return false;
			return varP !== normalizedIndicator;
		});

		if (mismatched.length === 0) return;

		const mismatchNames = mismatched
			.map((c) => c.GoalsVariable?.name || c.name || c.goals_variables_id)
			.slice(0, 8)
			.join(", ");

		SendaCustomSwal.fire({
			iconHtml: '<i class="error-icon"></i>',
			title: "Periodicidade alterada",
			text: `Algumas variáveis importadas possuem periodicidade diferente e foram removidas: ${mismatchNames}${
				mismatched.length > 8 ? "..." : ""
			}. ${
				isLocalMode
					? "Isso afeta apenas o rascunho até você salvar o indicador."
					: "As alterações serão aplicadas ao salvar o indicador."
			}`,
			customClass: { confirmButton: "custom-confirm-button" },
		});

		(async () => {
			if (isLocalMode) {
				setConfigs((prev) =>
					prev.filter(
						(c) =>
							!(
								c.goals_variables_id &&
								c.GoalsVariable &&
								mismatched.some((m) => m.id === c.id)
							),
					),
				);
				return;
			}

			// Fluxo remoto: NÃO remove no backend agora.
			// Mantém pendência pra aplicar somente quando o usuário clicar em "Salvar" no indicador.
			const idsToRemoveSet = new Set(
				mismatched.map((m) => m.id).filter(Boolean),
			);
			pendingRemoveIdsRef.current = idsToRemoveSet;
			setConfigs((prev) =>
				prev.filter((c) => !idsToRemoveSet.has(c.id)),
			);
		})();
	}, [indicatorPeriodicity, configs, isLocalMode]);

	useImperativeHandle(ref, () => ({
		savePendingConfigs: async (newGoalId) => {
			// Modo local: criação de configs pendentes após criação do indicador.
			if (isLocalMode && newGoalId) {
				const results = [];
				const createdWithSource = [];
				const oldIdToNewId = new Map();

				for (const config of configs) {
					const payload = {
						goals_id: newGoalId,
						goals_variables_id: config.goals_variables_id || null,
						name: config.name || null,
						quantifier: config.quantifier || null,
						type: config.type,
						formula: config.formula || null,
						display_on_chart: config.display_on_chart,
						sequence: config.sequence,
						color: config.color || getFallbackColorForConfig(config),
						type_meta_definition:
							config.type_meta_definition || null,
						totalizator: config.totalizator || null,
						totalizator_ref: null,
					};
					const result = await indicadoresService.addVariableConfig(payload);
					oldIdToNewId.set(String(config.id), result.id);
					createdWithSource.push({ result, source: config });
					results.push(result);
				}

				for (const { result, source } of createdWithSource) {
					if (!source.totalizator_ref) continue;
					const newRefId = oldIdToNewId.get(
						String(source.totalizator_ref),
					);
					if (!newRefId) continue;
					await indicadoresService.updateVariableConfig(result.id, {
						totalizator_ref: newRefId,
					});
					result.totalizator_ref = newRefId;
				}

				setConfigs(results);
				return results;
			}

			// Modo remoto: aplicar updates pendentes (campos editados) e
			// depois remoções pendentes (por mismatch de periodicidade).
			if (!isLocalMode) {
				const updates = pendingUpdatesRef.current || {};
				const updateEntries = Object.entries(updates);

				if (updateEntries.length > 0) {
					await Promise.all(
						updateEntries.map(([configId, fields]) =>
							indicadoresService.updateVariableConfig(configId, fields),
						),
					);
					pendingUpdatesRef.current = {};
				}

				const idsToRemove = Array.from(pendingRemoveIdsRef.current);
				if (idsToRemove.length > 0) {
					await Promise.all(
						idsToRemove.map((id) =>
							indicadoresService.removeVariableConfig(id),
						),
					);
					pendingRemoveIdsRef.current = new Set();
				}

				return { updated: updateEntries.map(([id]) => id), removed: idsToRemove };
			}

			return [];
		},
	}));

	const fetchConfigs = async () => {
		setLoading(true);
		try {
			const response = await indicadoresService.getVariablesConfig(goalId);
			setConfigs(response || []);
			pendingUpdatesRef.current = {};
			pendingRemoveIdsRef.current = new Set();
		} catch (error) {
			console.error("Erro ao buscar configurações:", error);
		} finally {
			setLoading(false);
		}
	};

	const handleVariableAdded = (newConfig) => {
		setConfigs((prev) => [...prev, newConfig]);
	};

	const handleOpenCreateCalculatedModal = () => {
		setEditingCalculatedConfig(null);
		setIsCalcModalOpen(true);
	};

	const handleOpenEditCalculatedModal = (config) => {
		setEditingCalculatedConfig(config);
		setIsCalcModalOpen(true);
	};

	const handleCloseCalculatedModal = () => {
		setIsCalcModalOpen(false);
		setEditingCalculatedConfig(null);
	};

	const handleRemove = async (config) => {
		const variableName =
			config.goals_variables_id && config.GoalsVariable
				? config.GoalsVariable.name
				: config.name || "esta variável";

		const result = await SendaCustomSwal.fire({
			iconHtml: '<i class="warning-icon"></i>',
			title: "Remover variável",
			html: `<p style="margin:0;text-align:left;font-size:14px;color:#374151;">
				Tem certeza que deseja remover <strong>${variableName}</strong> deste indicador?
			</p>`,
			showCancelButton: true,
			confirmButtonText: "Sim, remover",
			cancelButtonText: "Cancelar",
			customClass: {
				confirmButton: "custom-confirm-button",
				cancelButton: "custom-cancel-button",
			},
		});

		if (!result.isConfirmed) return;

		if (isLocalMode) {
			setConfigs((prev) => prev.filter((c) => c.id !== config.id));
			return;
		}
		try {
			await indicadoresService.removeVariableConfig(config.id);
			setConfigs((prev) => prev.filter((c) => c.id !== config.id));
		} catch (error) {
			console.error("Erro ao remover:", error);
			SendaCustomSwal.fire({
				iconHtml: '<i class="error-icon"></i>',
				title: "Erro",
				text: "Não foi possível remover a variável.",
				customClass: { confirmButton: "custom-confirm-button" },
			});
		}
	};

	const handleUpdateConfig = (configId, field, value) => {
		setConfigs((prev) =>
			prev.map((c) => {
				if (c.id !== configId) return c;
				const next = { ...c, [field]: value };
				// Ao sair de sum/average, limpa a coluna referência
				if (
					field === "totalizator" &&
					value !== "sum" &&
					value !== "average"
				) {
					next.totalizator_ref = null;
				}
				return next;
			}),
		);

		if (isLocalMode) return;

		const current = pendingUpdatesRef.current[configId] || {};
		const pending = { ...current, [field]: value };
		if (
			field === "totalizator" &&
			value !== "sum" &&
			value !== "average"
		) {
			pending.totalizator_ref = null;
		}
		pendingUpdatesRef.current[configId] = pending;
	};

	const handleCalculatedVariableUpdated = (configId, fields) => {
		Object.entries(fields).forEach(([field, value]) => {
			handleUpdateConfig(configId, field, value);
		});
	};

	const existingVariableIds = configs
		.filter((c) => c.goals_variables_id)
		.map((c) => Number(c.goals_variables_id));

	const getVariableName = (config) => {
		if (config.goals_variables_id && config.GoalsVariable) {
			return config.GoalsVariable.name;
		}
		return config.name || "—";
	};

	const getVariableUnit = (config) => {
		if (config.goals_variables_id && config.GoalsVariable) {
			return config.GoalsVariable.quantifier || "—";
		}
		return config.quantifier || "—";
	};

	const getVariablePeriodicity = (config) => {
		if (config.goals_variables_id && config.GoalsVariable) {
			return config.GoalsVariable.periodicity || "—";
		}
		return null;
	};

	const isCalculated = (config) => !config.goals_variables_id;

	return (
		<div
			className="container-fluid"
			style={{
				marginTop: 20,
				background: "#f3f3f9",
				borderRadius: 10,
				padding: 20,
			}}
		>
			<div
				style={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					marginBottom: 16,
				}}
			>
				<h5 style={{ margin: 0, fontWeight: 700 }}>Configuração de Variáveis do Indicador</h5>
				<div style={{ display: "flex", gap: 8 }}>
					<Button
						onClick={() => setIsAddModalOpen(true)}
						style={{ background: "none", color: "black", display: "flex", alignItems: "center", gap: 4, padding: 10 }}
					>
						<Plus size={16} /> Adicionar Variável
					</Button>
					<Button
						onClick={handleOpenCreateCalculatedModal}
						style={{ background: "none", color: "black", display: "flex", alignItems: "center", gap: 4, padding: 10 }}
					>
						<Plus size={16} /> Criar Variável Calculada
					</Button>
				</div>
			</div>

			{loading ? (
				<div style={{ display: "flex", justifyContent: "center", padding: "40px 0", gap: 8 }}>
					<span className="spinner-border spinner-border-sm" style={{ marginRight: 10 }} />
					Carregando...
				</div>
			) : configs.length === 0 ? (
				<div
					style={{
						border: "2px dashed #dee2e6",
						borderRadius: 8,
						padding: "40px 20px",
						textAlign: "center",
					}}
				>
					<div style={{ color: "#6c757d", marginBottom: 12 }}>
						Nenhuma variável configurada
					</div>
					<Button
						onClick={() => setIsAddModalOpen(true)}
						style={{ background: "none", color: "black", display: "inline-flex", alignItems: "center", gap: 4, padding: 10 }}
					>
						<Plus size={16} /> Adicionar Primeira Variável
					</Button>
				</div>
			) : (
				<div>
					{configs.map((config) => (
						<div
							key={config.id}
							style={{
								backgroundColor: "white",
								borderRadius: 8,
								border: "1px solid #dee2e6",
								padding: "12px 16px",
								marginBottom: 8,
							}}
						>
							{/* Linha 1: cor + informações somente leitura */}
							<div
								style={{
									display: "flex",
									alignItems: "flex-start",
									gap: 12,
									flexWrap: "wrap",
								}}
							>
								<div>
									<div style={{ fontSize: 11, color: "#6c757d", marginBottom: 2 }}>Cor</div>
									<Input
										type="color"
										value={config.color || getFallbackColorForConfig(config)}
										onChange={(e) => handleUpdateConfig(config.id, "color", e.target.value)}
										style={{
											width: 36,
											height: 36,
											padding: 2,
											border: "1px solid #dee2e6",
											borderRadius: 4,
											cursor: "pointer",
										}}
									/>
								</div>
								<div style={{ flex: 1, minWidth: 140 }}>
									<div style={{ fontSize: 11, color: "#6c757d", marginBottom: 2 }}>Variável</div>
									<div
										style={{
											padding: "7px 10px",
											backgroundColor: "#f8f9fa",
											border: "1px solid #dee2e6",
											borderRadius: 4,
											fontSize: 13,
											fontWeight: 600,
											overflow: "hidden",
											textOverflow: "ellipsis",
											whiteSpace: "nowrap",
										}}
										title={getVariableName(config)}
									>
										{getVariableName(config)}
									</div>
								</div>
								{isCalculated(config) && (
									<div style={{ flex: 1, minWidth: 140 }}>
										<div style={{ fontSize: 11, color: "#6c757d", marginBottom: 2 }}>Fórmula</div>
										<div
											style={{
												padding: "7px 10px",
												backgroundColor: "#f8f9fa",
												border: "1px solid #dee2e6",
												borderRadius: 4,
												fontSize: 13,
												overflow: "hidden",
												textOverflow: "ellipsis",
												whiteSpace: "nowrap",
												fontFamily: "monospace",
											}}
											title={config.formula || ""}
										>
											{config.formula || "—"}
										</div>
									</div>
								)}
								<div style={{ minWidth: 80 }}>
									<div style={{ fontSize: 11, color: "#6c757d", marginBottom: 2 }}>Quantificador</div>
									<div
										style={{
											padding: "7px 10px",
											backgroundColor: "#f8f9fa",
											border: "1px solid #dee2e6",
											borderRadius: 4,
											fontSize: 13,
											textAlign: "center",
										}}
									>
										{getVariableUnit(config)}
									</div>
								</div>
								<div
									style={{
										paddingTop: 16,
										display: "flex",
										alignItems: "center",
										gap: 2,
										marginLeft: "auto",
									}}
								>
									{isCalculated(config) && (
										<Button
											color="link"
											style={{ color: "black", padding: 4 }}
											onClick={() => handleOpenEditCalculatedModal(config)}
											title="Editar variável calculada"
										>
											<Pencil size={18} />
										</Button>
									)}
									<Button
										color="link"
										style={{ color: "black", padding: 4 }}
										onClick={() => handleRemove(config)}
										title="Remover"
									>
										<Trash size={18} />
									</Button>
								</div>
							</div>

							{/* Linha 2: campos editáveis */}
							<div
								style={{
									display: "flex",
									gap: 12,
									flexWrap: "wrap",
									alignItems: "flex-start",
									marginTop: 10,
								}}
							>
								<div style={{ minWidth: 140, flex: 1 }}>
									<div style={{ fontSize: 11, color: "#6c757d", marginBottom: 2 }}>Tipo</div>
									<Select
										styles={customStyles}
										value={typeOptions.find((o) => o.value === config.type)}
										onChange={(opt) => {
											handleUpdateConfig(config.id, "type", opt.value);
											if (opt.value !== "0") {
												handleUpdateConfig(config.id, "type_meta_definition", null);
											}
										}}
										options={typeOptions}
										menuPortalTarget={document.body}
										menuPosition="fixed"
									/>
								</div>
								{config.type === "0" && (
									<div style={{ minWidth: 180, flex: 1 }}>
										<div style={{ fontSize: 11, color: "#6c757d", marginBottom: 2 }}>Definição da Meta</div>
										<div
											style={{
												display: "flex",
												alignItems: "stretch",
												height: 38,
												borderRadius: 4,
												overflow: "hidden",
												border: "1px solid #dee2e6",
											}}
										>
											<button
												type="button"
												onClick={() => handleUpdateConfig(config.id, "type_meta_definition", "maior_melhor")}
												style={{
													flex: 1,
													border: "none",
													outline: "none",
													margin: 0,
													padding: "6px 8px",
													fontSize: 11,
													fontWeight: 600,
													cursor: "pointer",
													whiteSpace: "nowrap",
													backgroundColor: config.type_meta_definition === "maior_melhor" ? "#212529" : "white",
													color: config.type_meta_definition === "maior_melhor" ? "white" : "#6c757d",
												}}
											>
												Maior Melhor
											</button>
											<button
												type="button"
												onClick={() => handleUpdateConfig(config.id, "type_meta_definition", "menor_melhor")}
												style={{
													flex: 1,
													border: "none",
													borderLeft: "1px solid #dee2e6",
													outline: "none",
													margin: 0,
													padding: "6px 8px",
													fontSize: 11,
													fontWeight: 600,
													cursor: "pointer",
													whiteSpace: "nowrap",
													backgroundColor: config.type_meta_definition === "menor_melhor" ? "#212529" : "white",
													color: config.type_meta_definition === "menor_melhor" ? "white" : "#6c757d",
												}}
											>
												Menor Melhor
											</button>
										</div>
									</div>
								)}
								<div style={{ minWidth: 140, flex: 1 }}>
									<div style={{ fontSize: 11, color: "#6c757d", marginBottom: 2 }}>Exibir no Gráfico</div>
									<Select
										styles={customStyles}
										value={graphicType.find(
											(o) => o.value === Number(config.display_on_chart),
										)}
										onChange={(opt) =>
											handleUpdateConfig(config.id, "display_on_chart", opt.value)
										}
										options={graphicType}
										menuPortalTarget={document.body}
										menuPosition="fixed"
									/>
								</div>
								<div style={{ minWidth: 140, flex: 1 }}>
									<div style={{ fontSize: 11, color: "#6c757d", marginBottom: 2 }}>
										Totalizador
									</div>
									<Select
										styles={customStyles}
										value={
											totalizadorInfos.find(
												(o) =>
													o.value ===
													(config.totalizator || ""),
											) || totalizadorInfos[0]
										}
										onChange={(opt) =>
											handleUpdateConfig(
												config.id,
												"totalizator",
												opt.value,
											)
										}
										options={totalizadorInfos}
										menuPortalTarget={document.body}
										menuPosition="fixed"
									/>
								</div>
								<div style={{ minWidth: 160, flex: 1 }}>
									<div style={{ fontSize: 11, color: "#6c757d", marginBottom: 2 }}>
										Coluna Referência
									</div>
									<Select
										styles={customStyles}
										isDisabled={
											String(config.type) === "1" ||
											(config.totalizator !== "sum" &&
												config.totalizator !== "average")
										}
										value={(() => {
											const refOpts = [
												{ value: "", label: "Nenhum" },
												...configs
													.filter(
														(c) =>
															c.id !== config.id &&
															String(c.type) === "1",
													)
													.map((c) => ({
														value: c.id,
														label: getVariableName(c),
													})),
											];
											return (
												refOpts.find(
													(o) =>
														o.value ===
															config.totalizator_ref ||
														String(o.value) ===
															String(
																config.totalizator_ref ||
																	"",
															),
												) || refOpts[0]
											);
										})()}
										onChange={(opt) =>
											handleUpdateConfig(
												config.id,
												"totalizator_ref",
												opt.value || null,
											)
										}
										options={[
											{ value: "", label: "Nenhum" },
											...configs
												.filter(
													(c) =>
														c.id !== config.id &&
														String(c.type) === "1",
												)
												.map((c) => ({
													value: c.id,
													label: getVariableName(c),
												})),
										]}
										menuPortalTarget={document.body}
										menuPosition="fixed"
									/>
								</div>
							</div>
							{!isCalculated(config) && (
								<div
									style={{
										marginTop: 8,
										padding: "6px 10px",
										backgroundColor: "#f8f9fa",
										borderRadius: 4,
										fontSize: 12,
										color: "#6c757d",
										display: "flex",
										alignItems: "center",
										gap: 6,
									}}
								>
									Valores lançados via "Lançar Variáveis" – Periodicidade:{" "}
									<strong>{getVariablePeriodicity(config)}</strong> – Quantificador sincronizado
									com a variável
								</div>
							)}
						</div>
					))}
				</div>
			)}

			<AddVariableModal
				isOpen={isAddModalOpen}
				onClose={() => setIsAddModalOpen(false)}
				goalId={goalId}
				existingVariableIds={existingVariableIds}
				existingConfigs={configs}
				indicatorPeriodicity={indicatorPeriodicity}
				onVariableAdded={handleVariableAdded}
			/>

			<CalculatedVariableModal
				isOpen={isCalcModalOpen}
				onClose={handleCloseCalculatedModal}
				goalId={goalId}
				availableVariables={configs}
				editingConfig={editingCalculatedConfig}
				onVariableCreated={handleVariableAdded}
				onVariableUpdated={handleCalculatedVariableUpdated}
			/>
		</div>
	);
});

export default VariableConfiguration;
